import React from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Creatable from "react-select/lib/Creatable";
import { useSelector, useDispatch } from "react-redux";
import { buildOptions } from "../utils";
import { uniq } from "lodash";
import useFetch from "../fetch";
import { sendStaffAppMetaData } from "../staffApp/eventTriggers";

function ContactFormModal() {
  const props = useSelector((state) => {
    return {
      show: state.ui.contactModal.show,
      contact: state.ui.contactModal.contact,
      action: state.ui.contactModal.action,
      organizationName: state.organization.name,
      resident: state.resident,
      isStaffApp: state.config.isStaffApp,
      contact_type_options: state.org_dependant_data.contact_type_options,
      contactTabDefaultMetaData: state.config.tab_default_meta_data.find(
        (tab) => tab.detail == "contacts"
      ),
      hasDelete: state.current_user.permissions.includes("Delete"),
      hasEdit: state.current_user.permissions.includes("Edit"),
    };
  });
  const [submittingForm, setSubmittingForm] = React.useState(false);
  const dispatch = useDispatch();
  const fetch = useFetch();
  const closeModal = () =>
    dispatch({
      type: "CLOSE_RESOURCE_MODAL",
      payload: { resourceName: "contact" },
    });
  const updateField = (name, value) =>
    dispatch({
      type: "UPDATE_RESOURCE_FIELD",
      payload: { resourceName: "contact", name, value },
    });
  const updateContacts = (contacts) =>
    dispatch({ type: "UPDATE_CONTACTS", payload: { contacts } });
  const createContact = (contact) =>
    dispatch({ type: "CREATE_CONTACT", payload: { contact } });
  const deleteContact = (contact) =>
    dispatch({ type: "DELETE_CONTACT", payload: { contact } });
  const setDefaultStaffAppMetaData = () =>
    sendStaffAppMetaData(props.contactTabDefaultMetaData);

  const updateResidentContactsAction = () => {
    return (dispatch, getState) => {
      const state = getState();
      return fetch(
        `/organizations/${state.organization.name}/residents/${state.resident.id}.json`,
        {
          method: "PATCH",
          headers: {'Content-Type': 'application/json'},
          data: JSON.stringify({
            resident: {
              contacts_attributes: [state.ui.contactModal.contact]
            }
          })
        }
      ).then((data) => {
        dispatch({
          type: "UPDATE_CONTACTS",
          payload: { contacts: data.contacts },
        });
      });
    };
  };

  const updateResidentContacts = (contact) => {
    const { organizationName, resident } = props;
    return fetch(
      `/organizations/${organizationName}/residents/${resident.id}.json`,
      {
        method: "PATCH",
        headers: {'Content-Type': 'application/json'},
        data: JSON.stringify({
          resident: {
            contacts_attributes: [contact]
          },
        })
      }
    );
  };

  const removeContact = () => {
    setSubmittingForm(true);
    updateResidentContacts({ id: props.contact.id, _destroy: true }).then(
      () => {
        deleteContact(props.contact);
        if (props.isStaffApp) alert("Contact successfully deleted.");
        setDefaultStaffAppMetaData();
        setSubmittingForm(false);
        closeModal();
      }
    );
  };

  const saveContact = () => {
    setSubmittingForm(true);

    dispatch(updateResidentContactsAction()).then((data) => {
      if (props.isStaffApp) alert("Contact changes saved.");
      setDefaultStaffAppMetaData();
      setSubmittingForm(false);
      closeModal();
    });
  };

  const { name, contact_kind_list = [], phone, email, consent, notes } = props.contact;
  const kindOptions = buildOptions(props.contact_type_options);
  const kindValue = kindOptions.find((option) => option.value === contact_kind_list[0]);

  React.useEffect(() => {
    let saveContactFunction = (e) => {
      saveContact();
    };
    document.addEventListener("saveContact", saveContactFunction);
    return () =>
      document.removeEventListener("saveContact", saveContactFunction);
  }, []);

  return (
    <Modal
      animation={!props.isStaffApp}
      show={props.show}
      onHide={() => closeModal()}
    >
      {!props.isStaffApp && (
        <Modal.Header closeButton>
          <Modal.Title>Contact</Modal.Title>
        </Modal.Header>
      )}
      <Modal.Body className={props.hasEdit ? "p-4" : "p-4 form-modal-width"}>
        {props.hasEdit ? (
          <>
            <Form.Group controlId="contactName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => {
                  updateField("name", e.target.value);
                }}
              />
            </Form.Group>
            <Form.Group controlId="contactKind">
              <Form.Label>Type</Form.Label>
              <Creatable
                options={kindOptions}
                value={kindValue}
                onChange={(option) => {
                  updateField("contact_kind_list", option.value);
                }}
              />
            </Form.Group>
            <Form.Group controlId="contactPhone">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                value={phone}
                onChange={(e) => {
                  updateField("phone", e.target.value);
                }}
              />
            </Form.Group>
            <Form.Group controlId="contactEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                value={email}
                onChange={(e) => {
                  updateField("email", e.target.value);
                }}
              />
            </Form.Group>
            <Form.Group className="ml-4">
              <Form.Check
                type="checkbox"
                id="contactConsent"
                label="Consent"
                checked={consent}
                onChange={(e) => {
                  updateField("consent", e.target.checked);
                }}
              />
            </Form.Group>
            <Form.Group controlId="contactPhone">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                value={notes}
                onChange={(e) => {
                  updateField("notes", e.target.value);
                }}
              />
            </Form.Group>
          </>
        ) : (
          <>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <div className="form-control-plaintext">{name || 'N/A'}</div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <div className="form-control-plaintext">{contact_kind_list[0] || 'N/A'}</div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Phone</Form.Label>
              <div className="form-control-plaintext">{phone || 'N/A'}</div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <div className="form-control-plaintext">{email || 'N/A'}</div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Consent</Form.Label>
              <div className="form-control-plaintext">{consent ? 'Yes' : 'No'}</div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <div className="form-control-plaintext">{notes || 'N/A'}</div>
            </Form.Group>
          </>
        )}
      </Modal.Body>
          {!props.isStaffApp ? (
            <Modal.Footer>
              {props.action === "edit" && props.hasDelete && (
                <Button
                  disabled={submittingForm}
                  variant="outline-danger"
                  onClick={() => removeContact(props.contact)}
                >
                  Delete
                </Button>
              )}
              <Button
                variant="info"
                disabled={submittingForm}
                onClick={() => saveContact()}>
                {props.action === "edit" ? "Update" : "Create"}
              </Button>
            </Modal.Footer>
          ) : (
            <div className="p-4">
              {props.action == "edit" && props.hasDelete && (
                <Button
                  disabled={submittingForm}
                  onClick={() => removeContact(props.contact)}
                  variant="outline-danger"
                  className="w-100"
                >
                  Delete
                </Button>
              )}
            </div>
      )}
    </Modal>
  );
}

export default ContactFormModal;
